import { createSlice } from '@reduxjs/toolkit'
import shuffle from 'lodash/shuffle'
import sortBy from 'lodash/sortBy'

import {
  backToPro,
  cancelSubscription,
  fetchCancellationReasonsList,
  fetchPro,
  getPrices,
  updateBillingEmail,
} from 'common/billing/actions/billingActions'
import { applyCoupon, resetCoupon } from 'common/billing/actions/couponActions'
import { migratePaidToV3 } from 'common/billing/actions/upgradeActions'
import { ProInfo, ProInfoBase } from 'common/billing/entities/pro/interfaces'
import { convertPro } from 'common/billing/entities/pro/utils/convertPro'
import { fetchSettings } from 'common/settings/actions'
import { BillingProSettings, CancellationReason } from 'shared/api/requests/billing/schemas'

const initialState: Readonly<ProInfo> = {}

const updateProState = <
  Action extends {
    payload: {
      pro: BillingProSettings
    }
  },
>(
  state: ProInfo,
  action: Action,
): ProInfoBase => ({
  ...state,
  ...(convertPro(action.payload?.pro) as ProInfoBase),
})

export const billingProSlice = createSlice({
  name: 'pro',
  initialState: initialState as ProInfo,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(resetCoupon.fulfilled, (state, action) => ({
        ...updateProState(state, action),
        coupon: undefined,
      }))
      .addCase(applyCoupon.fulfilled, (state, action) => ({
        ...state,
        ...updateProState(state, action),
        coupon: action.payload.coupon_info,
      }))
      .addCase(cancelSubscription.fulfilled, updateProState)
      .addCase(backToPro.fulfilled, updateProState)
      .addCase(fetchPro.fulfilled, updateProState)
      .addCase(migratePaidToV3.fulfilled, (state, action) => ({
        ...state,
        ...(convertPro(action.payload.data) as ProInfoBase),
      }))
      .addCase(fetchCancellationReasonsList.pending, (state) => {
        state.fetchingCancelReasons = true
      })
      .addCase(fetchCancellationReasonsList.rejected, (state) => {
        state.fetchingCancelReasons = false
      })
      .addCase(fetchCancellationReasonsList.fulfilled, (state, action) => {
        state.fetchingCancelReasons = false
        state.cancellationReasonsList = sortBy(
          shuffle(action.payload.reasons),
          (item: CancellationReason) =>
            ['other_main', 'other', 'removed_other', 'covid_other'].includes(item.id) ||
            item.id.endsWith('_other'),
        )
      })
      .addCase(updateBillingEmail.fulfilled, (state, action) => {
        state.billing_email = action.meta.arg
      })
      .addCase(getPrices.fulfilled, (state, action) => {
        const { automation_price, inbox_price } = action.payload

        state.automationPrice = automation_price
        state.inboxPrice = inbox_price
      })
      .addCase(fetchSettings.fulfilled, (state, action) => {
        return { ...state, ...convertPro(action.payload.settings.pro || {}) }
      })
  },
})
